<template>
  <div class="feature-blocks">
    <section
      v-for="(block, idx) in blocks"
      :key="block.title"
      class="feature-section"
      :class="[{ alt: idx % 2 === 1 }, block.reverse ? 'reverse' : '']"
    >
      <div class="section-inner">
        <div class="feature-content">
          <div class="feature-header">
            <img class="feature-icon" :alt="block.iconAlt" :src="block.iconSrc" />
            <h3 class="feature-title">{{ block.title }}</h3>
          </div>
          <p class="feature-description">{{ block.description }}</p>

          <div class="feature-list">
            <div v-for="item in block.bullets" :key="item" class="feature-item">
              <img
                class="check-icon"
                alt="Check"
                src="https://c.animaapp.com/nNxA75tB/img/circle-check-big-7.svg"
              />
              <p class="feature-item-text">{{ item }}</p>
            </div>
          </div>
        </div>

        <div class="feature-image">
          <img
            :alt="block.imageAlt"
            :src="block.imageSrc"
            :class="[
              block.imageFit === 'contain' ? 'contain' : '',
              block.imagePosition ? `pos-${block.imagePosition}` : '',
            ]"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

type FeatureBlock = {
  title: string;
  description: string;
  bullets: string[];
  iconSrc: string;
  iconAlt: string;
  imageSrc: string;
  imageAlt: string;
  imageFit?: "cover" | "contain";
  imagePosition?: "center" | "top" | "bottom";
  reverse?: boolean;
};

export default defineComponent({
  name: "FeatureBlocksSubsection",
  data() {
    // Public assets are served from /public and should be referenced via BASE_URL for correct builds.
    const publicBase = process.env.BASE_URL || "/";

    const blocks: FeatureBlock[] = [
      {
        title: "Caisse intuitive et rapide",
        description:
          "Simplifiez vos transactions avec une interface de point de vente conçue pour la rapidité et l'efficacité. Facile à utiliser, elle intègre la gestion des bouquets, des produits additionnels et des options de paiement variées.",
        bullets: [
          "Interface tactile optimisée pour un service rapide",
          "Gestion facile des produits et prixs",
          "Raports de vente détaillés",
          "Multiples options de paiement (espèces, carte, mobile, sans contact...)",
        ],
        iconSrc: "https://c.animaapp.com/nNxA75tB/img/shopping-cart.svg",
        iconAlt: "Icône caisse",
        imageSrc: `${publicBase}assets/fonctionnalites/caisse.webp`,
        imageAlt: "Caisse et écran",
        imageFit: "contain",
      },
      {
        title: "Gestion des commandes automatisée",
        description:
          "De la prise de commande à la livraison, Florisuite automatise chaque étape. Réduisez les erreurs et gagnez du temps grâce à un suivi précis et des rappels automatiques.",
        bullets: [
          "Suivi de commande détaillé pour chaque client",
          "Planification et optimisation des itinéraires de livraison",
          "Notifications automatiques pour les clients et les livreurs",
          "Intégration facile avec votre planning interne",
        ],
        iconSrc: "https://c.animaapp.com/nNxA75tB/img/message-square.svg",
        iconAlt: "Icône commandes",
        imageSrc: `${publicBase}assets/fonctionnalites/commandes.webp`,
        imageAlt: "Gestion des commandes",
        imageFit: "cover",
        reverse: true,
      },
      {
        title: "Facturation simplifiée et conforme",
        description:
          "Émettez des factures professionnelles et conformes en quelques clics. Automatisez vos relances et suivez vos paiements sans effort. Finie la paperasse !",
        bullets: [
          "Création de factures et devis personnalisables",
          "Suivi des paiements et gestion des impayés",
          "Exportation comptable pour une conformité fiscale simplifiée",
          "Archivage sécurisé de toutes vos transactions",
        ],
        iconSrc: "https://c.animaapp.com/nNxA75tB/img/receipt.svg",
        iconAlt: "Icône facturation",
        imageSrc: `${publicBase}assets/fonctionnalites/facturation.webp`,
        imageAlt: "Facturation",
        imageFit: "cover",
        imagePosition: "top",
      },
      {
        title: "Programmes de fidélisation innovants",
        description:
          "Fidélisez vos clients avec des offres personnalisées et des programmes de points. Transformez vos clients occasionnels en ambassadeurs de votre marque.",
        bullets: [
          "Création de cartes de fidélité virtuelles ou physiques",
          "Offres promotionnelles ciblées basées sur l'historique d'achat",
          "Système de points et de récompenses personnalisable",
          "Communication automatisée pour les anniversaires et événements spéciaux",
        ],
        iconSrc: "https://c.animaapp.com/nNxA75tB/img/heart-handshake-1.svg",
        iconAlt: "Icône fidélité",
        imageSrc: `${publicBase}assets/fonctionnalites/fidelisation.webp`,
        imageAlt: "Fidélisation",
        imageFit: "contain",
        reverse: true,
      },
      {
        title: "E-commerce synchronisé en temps réel",
        description:
          "Votre boutique en ligne est automatiquement mise à jour avec vos produits et vos prix. Offrez à vos clients une expérience d'achat fluide et sans accroc, 24h/24 et 7j/7.",
        bullets: [
          "Synchronisation automatique des produits",
          "Interface de gestion de boutique en ligne intuitive",
          "Gestion des commandes web et physique centralisée",
          "Optimisation SEO pour une meilleure visibilité en ligne",
        ],
        iconSrc: "https://c.animaapp.com/nNxA75tB/img/laptop.svg",
        iconAlt: "Icône e-commerce",
        imageSrc: `${publicBase}assets/fonctionnalites/ecommerce.webp`,
        imageAlt: "E-commerce",
        imageFit: "contain",
      },
      {
        title: "Gestion simplifiée des réseaux sociaux",
        description:
          "Planifiez, publiez et analysez vos posts sur toutes les plateformes depuis un seul tableau de bord. Possibilité avec l'IA. Renforcez votre présence en ligne et engagez votre communauté.",
        bullets: [
          "Publication et planification multi-plateformes (Instagram, Facebook, Tik Tok, Pinterest...)",
          "Analyse des performances de vos publications",
          "Création de campagnes publicitaires ciblées",
          "Gestion des interactions et des commentaires clients",
        ],
        iconSrc: "https://c.animaapp.com/nNxA75tB/img/megaphone.svg",
        iconAlt: "Icône réseaux sociaux",
        imageSrc: `${publicBase}assets/fonctionnalites/reseaux-sociaux.webp`,
        imageAlt: "Réseaux sociaux",
        imageFit: "cover",
        reverse: true,
      },
      {
        title: "Support client dédié et réactif",
        description:
          "Bénéficiez d'une assistance personnalisée pour toutes vos questions. Notre équipe est là pour vous accompagner et assurer le bon fonctionnement de votre activité.",
        bullets: [
          "Accès rapide à notre équipe de support par chat ou téléphone",
          "Base de connaissances complète et tutoriels vidéo",
          "Mises à jour régulières et améliorations",
          "Formation continue pour optimiser votre utilisation de Florisuite",
        ],
        iconSrc: "https://c.animaapp.com/nNxA75tB/img/message-square.svg",
        iconAlt: "Icône support",
        imageSrc: `${publicBase}assets/fonctionnalites/support.webp`,
        imageAlt: "Support",
        imageFit: "contain",
      },
    ];

    return { blocks };
  },
});
</script>

<style>
.feature-section {
  align-items: center;
  align-self: stretch;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 100px 30px;
  position: relative;
  width: 100%;
}

.feature-section.alt {
  background-color: #f1faea;
}

.feature-section .section-inner {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 64px;
  justify-content: center;
  max-width: 1200px;
  width: 100%;
}

.feature-section.reverse .section-inner {
  flex-direction: row-reverse;
}

.feature-section .feature-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 31px;
  min-width: 350px;
}

.feature-section .feature-header {
  align-items: center;
  display: flex;
  gap: 16px;
}

.feature-section .feature-icon {
  flex-shrink: 0;
  height: 40px;
  width: 40px;
}

.feature-section .feature-title {
  color: #1a1a1a;
  font-family: "Montserrat", Helvetica;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 36px;
  margin: 0;
}

.feature-section .feature-description {
  color: #5f5f5f;
  font-family: "Open Sans", Helvetica;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 28px;
  margin: 0;
}

.feature-section .feature-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 350px;
  width: 100%;
}

.feature-section .feature-item {
  align-items: center;
  display: flex;
  gap: 12px;
  width: 100%;
}

.feature-section .check-icon {
  height: 20px;
  width: 20px;
}

.feature-section .feature-item-text {
  color: #5f5f5f;
  flex: 1;
  font-family: "Open Sans", Helvetica;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 24px;
  margin: 0;
}

.feature-section .feature-image {
  aspect-ratio: 560/418;
  border-radius: 10px;
  flex: 1;
  min-height: 261px;
  min-width: 350px;
  overflow: hidden;
}

.feature-section .feature-image img {
  display: block;
  height: 100%;
  object-fit: cover;
  object-position: center;
  width: 100%;
}

.feature-section .feature-image img.contain {
  object-fit: contain;
}

.feature-section .feature-image img.pos-top {
  object-position: center top;
}

.feature-section .feature-image img.pos-bottom {
  object-position: center bottom;
}
</style>


